import { AlertTriangle, ChevronRight, Truck } from "lucide-react";

import type { CarrierTransportServiceRow } from "@/api/carrier";
import { LoadingRows } from "@/components/data-table";
import { StatusBadge } from "@/components/status-badge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate } from "@/lib/format";

interface ShippingServiceCatalogueProps {
  rows: CarrierTransportServiceRow[];
  initialLoading: boolean;
  hasFilters: boolean;
  onView: (service: CarrierTransportServiceRow) => void;
  onEdit: (service: CarrierTransportServiceRow) => void;
}

/** Compact list/table presentation shared by responsive shipping-service routes. */
export function ShippingServiceCatalogue({
  rows,
  initialLoading,
  hasFilters,
  onView,
  onEdit,
}: ShippingServiceCatalogueProps) {
  return (
    <>
      <div className="md:hidden">
        {initialLoading ? (
          <div role="status" className="p-8 text-center text-sm">
            Loading shipping services…
          </div>
        ) : rows.length > 0 ? (
          <ul aria-label="Shipping service summaries" className="divide-y">
            {rows.map((service) => (
              <li key={service.id}>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-auto min-h-16 w-full justify-start gap-3 rounded-none px-4 py-3 text-left whitespace-normal hover:bg-muted/50"
                  aria-label={`View ${service.name}`}
                  onClick={() => onView(service)}
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium">
                        {service.name}
                      </span>
                      <StatusBadge
                        status={service.isActive ? "ACTIVE" : "INACTIVE"}
                      />
                    </span>
                    <span className="mt-1 block truncate font-mono text-xs text-muted-foreground">
                      {service.code} · {formatEta(service.estimatedDays)}
                    </span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </Button>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState filtered={hasFilters} />
        )}
      </div>

      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Estimated delivery</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Limits</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {initialLoading ? (
              <LoadingRows columns={6} rows={3} />
            ) : rows.length > 0 ? (
              rows.map((service) => (
                <TableRow key={service.id}>
                  <TableCell className="font-mono text-xs">
                    {service.code}
                  </TableCell>
                  <TableCell className="font-medium">{service.name}</TableCell>
                  <TableCell>{formatEta(service.estimatedDays)}</TableCell>
                  <TableCell>
                    <StatusBadge
                      status={service.isActive ? "ACTIVE" : "INACTIVE"}
                    />
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatLimits(service)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label={`View details for ${service.name}`}
                        onClick={() => onView(service)}
                      >
                        View
                      </Button>
                      {service.selfRated ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          aria-label={`Edit ${service.name}`}
                          onClick={() => onEdit(service)}
                        >
                          Edit
                        </Button>
                      ) : (
                        <Badge variant="outline" className="font-normal">
                          API-managed
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} className="p-0">
                  <EmptyState filtered={hasFilters} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}

export function ShippingServiceDetails({
  service,
  onEdit,
}: {
  service: CarrierTransportServiceRow;
  onEdit?: () => void;
}) {
  const sources = service.sourceProviderIds?.length
    ? service.sourceProviderIds.join(", ")
    : "None";

  return (
    <div className="space-y-5">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-5 gap-y-2 text-sm">
        <dt className="text-muted-foreground">Status</dt>
        <dd>
          <StatusBadge status={service.isActive ? "ACTIVE" : "INACTIVE"} />
        </dd>
        <dt className="text-muted-foreground">Management</dt>
        <dd>{service.selfRated ? "Carrier-managed" : "API-managed"}</dd>
        <dt className="text-muted-foreground">Estimated delivery</dt>
        <dd>{formatEta(service.estimatedDays)}</dd>
        <dt className="text-muted-foreground">Maximum weight</dt>
        <dd>{formatMeasurement(service.maxWeightKg, "kg")}</dd>
        <dt className="text-muted-foreground">Longest side</dt>
        <dd>{formatMeasurement(service.maxLongestSideCm, "cm")}</dd>
        <dt className="text-muted-foreground">Length + girth</dt>
        <dd>{formatMeasurement(service.maxLengthPlusGirthCm, "cm")}</dd>
        <dt className="text-muted-foreground">DIM factor (cm)</dt>
        <dd>{formatValue(service.dimFactorCm)}</dd>
        <dt className="text-muted-foreground">DIM factor (inch)</dt>
        <dd>{formatValue(service.dimFactorInch)}</dd>
        <dt className="text-muted-foreground">Carrier</dt>
        <dd>{service.carrier?.name ?? service.carrierId ?? "—"}</dd>
        <dt className="text-muted-foreground">Source providers</dt>
        <dd className="break-all">{sources}</dd>
        <dt className="text-muted-foreground">First seen</dt>
        <dd>{formatDate(service.firstSeenAt)}</dd>
        <dt className="text-muted-foreground">Last seen</dt>
        <dd>{formatDate(service.lastSeenAt)}</dd>
        <dt className="text-muted-foreground">Created</dt>
        <dd>{formatDate(service.createdAt)}</dd>
        <dt className="text-muted-foreground">Updated</dt>
        <dd>{formatDate(service.updatedAt)}</dd>
      </dl>
      {onEdit ? (
        <Button size="touch" className="w-full" onClick={onEdit}>
          Edit service
        </Button>
      ) : (
        <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          This service is managed by its connected API provider.
        </p>
      )}
    </div>
  );
}

export function ShippingServicesPermissionState() {
  return (
    <div role="alert" className="p-8 text-center text-sm text-muted-foreground">
      <AlertTriangle className="mx-auto mb-2 h-9 w-9 text-destructive opacity-70" />
      You do not have permission to view shipping services.
    </div>
  );
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="p-8 text-center text-sm text-muted-foreground">
      <Truck className="mx-auto mb-2 h-9 w-9 opacity-50" />
      {filtered
        ? "No shipping services match these filters."
        : "No shipping services yet. Add one to start quoting."}
    </div>
  );
}

function formatEta(days: number | null | undefined): string {
  if (days == null) return "Not specified";
  return `${days} ${days === 1 ? "day" : "days"}`;
}

function formatValue(value: unknown): string {
  return value === null || value === undefined || value === ""
    ? "—"
    : String(value);
}

function formatMeasurement(value: unknown, unit: string): string {
  const formatted = formatValue(value);
  return formatted === "—" ? formatted : `${formatted} ${unit}`;
}

function formatLimits(service: CarrierTransportServiceRow): string {
  const limits = [
    service.maxWeightKg == null ? null : `${service.maxWeightKg} kg`,
    service.maxLongestSideCm == null
      ? null
      : `${service.maxLongestSideCm} cm side`,
  ].filter(Boolean);
  return limits.length ? limits.join(" · ") : "No limits set";
}
